if (!customElements.get("location-modal")) {
  customElements.define(
    "location-modal",
    class LocationModal extends HTMLElement {
      constructor() {
        super();

        this.tokenName = `localePopup:${window.location.hostname}`;
        this.buttonsContainer = this.querySelector(".js-buttons");
        this.closeButton = this.querySelector(".js-close");
        this.locationText = this.querySelector(".js-location-text");
        this.countryDataScript = this.querySelector("#CountryLocalizationData");
        this.countries = this.countryDataScript
          ? JSON.parse(this.countryDataScript.textContent)
          : [];

        this.route = window.Shopify?.routes?.root || "/";
        this.suggestions = {
          language: window.Shopify?.locale || "en",
          country: "",
          code: ""
        };

        this.locale = {
          code: window.Shopify?.country || "",
          language: window.Shopify?.locale || "en"
        };

        this.AED_REGION_CODES = ["AE", "SA", "QA", "KW", "BH", "OM"];
        this.UK_CODES = ["GB"];
        this.EUROPE_CODES = [
          "FR", "DE", "PL", "ES", "IT", "NL", "BE", "AT", "IE", "PT",
          "SE", "DK", "FI", "CZ", "RO", "GR", "HU", "SK", "SI", "LU",
          "HR", "BG", "EE", "LV", "LT", "MT", "CY"
        ];
      }

      connectedCallback() {
        if (this.closeButton) {
          this.closeButton.addEventListener("click", () => this.close());
        }

        this.initiate();
      }

      async initiate() {
        await this.checkRegion();

        if (!this.shouldShowModal()) return;

        const token = this.getToken(this.tokenName);
        const hasContextChanged = this.hasContextChanged(token);

        if (token && !hasContextChanged && !this.shouldIgnoreStoredPreference()) {
          return;
        }

        this.updatePopupContent();
        this.openModal();
      }

      async checkRegion() {
        try {
          const response = await fetch(this.route + "browsing_context_suggestions.json");
          const data = await response.json();

          this.setSuggestions(data);
        } catch (error) {
          console.error("[LocationModal] Failed to fetch browsing context suggestions", error);
        }
      }

      setSuggestions(response) {
        this.suggestions.country = response?.detected_values?.country?.name || "";
        this.suggestions.code = response?.detected_values?.country?.handle || "";

        const suggestedLanguage = response?.suggestions?.[0]?.parts?.language?.handle;

        if (suggestedLanguage) {
          this.suggestions.language = suggestedLanguage;
        }
      }

      isPrimaryStore() {
        return this.isCurrentStore(window.routes?.uk_redirect_url);
      }

      isUaeStore() {
        return this.isCurrentStore(window.routes?.uae_redirect_url);
      }

      isCurrentStore(storeUrl) {
        try {
          if (!storeUrl) return false;

          const currentOrigin = window.location.origin;
          const targetOrigin = new URL(storeUrl, window.location.origin).origin;

          return currentOrigin === targetOrigin;
        } catch (error) {
          console.error("[LocationModal] Failed to determine current store", error);
          return false;
        }
      }

      getCurrentStoreKey() {
        if (this.isPrimaryStore()) return "com";
        if (this.isUaeStore()) return "uae";
        return "unknown";
      }

      isUkCountry(code = this.suggestions.code) {
        return this.UK_CODES.includes(code);
      }

      isAedRegion(code = this.suggestions.code) {
        return this.AED_REGION_CODES.includes(code);
      }

      isEuropeanCountry(code = this.suggestions.code) {
        return this.EUROPE_CODES.includes(code);
      }

      hasSupportedMarket(code = this.suggestions.code) {
        return this.countries.some((country) => country.code === code);
      }

      shouldShowModal() {
        const code = this.suggestions.code;

        if (!code) return false;

        if (this.isUaeStore()) {
          if (this.isAedRegion(code)) return false;
          return true;
        }

        if (this.isPrimaryStore()) {
          if (this.isUkCountry(code)) return false;
          if (this.isAedRegion(code)) return true;
          if (this.hasSupportedMarket(code)) return true;
          return false;
        }

        return false;
      }

      shouldIgnoreStoredPreference() {
        if (this.isPrimaryStore() && this.isAedRegion()) {
          const token = this.getToken(this.tokenName);

          return !token?.selectedRegion || token.selectedRegion === "dismissed";
        }

        if (this.isUaeStore() && (this.isUkCountry() || this.isEuropeanCountry())) {
          const token = this.getToken(this.tokenName);

          return !token?.selectedRegion || token.selectedRegion === "dismissed";
        }

        return false;
      }

      hasContextChanged(token) {
        if (!token) return false;

        return (
          token.detectedCountry !== this.suggestions.code ||
          token.store !== this.getCurrentStoreKey()
        );
      }

      buildToken(value = {}) {
        return {
          ...value,
          detectedCountry: this.suggestions.code,
          store: this.getCurrentStoreKey()
        };
      }

      updatePopupContent() {
        this.updateLocationText();
        this.renderButtons();
      }

      updateLocationText() {
        if (!this.locationText) return;

        const countryName = this.suggestions.country || "your country";
        this.locationText.textContent = `We think you are in ${countryName}. Please choose your region and currency.`;
      }

      renderButtons() {
        if (!this.buttonsContainer) return;

        const buttons = this.getButtonsForContext();

        this.buttonsContainer.innerHTML = "";

        buttons.forEach((buttonConfig) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "button button--outline button--large button--full-width";
          button.textContent = buttonConfig.label;
          button.addEventListener("click", buttonConfig.onClick);

          this.buttonsContainer.appendChild(button);
        });
      }

      getButtonsForContext() {
        const code = this.suggestions.code;
        const buttons = [];

        if (this.isUaeStore()) {
          if (this.isUkCountry(code)) {
            buttons.push(this.getUkButton());
            buttons.push(this.getEuropeButton());
            buttons.push(this.getUaeButton());
            return buttons;
          }

          buttons.push(this.getEuropeButton());
          buttons.push(this.getUaeButton());
          return buttons;
        }

        if (this.isPrimaryStore()) {
          if (this.isAedRegion(code)) {
            buttons.push(this.getUaeButton());
            buttons.push(this.getUkButton());
            buttons.push(this.getEuropeButton());
            return buttons;
          }

          if (this.hasSupportedMarket(code)) {
            buttons.push(this.getDetectedMarketButton());
            buttons.push(this.getUkButton());
            return buttons;
          }
        }

        buttons.push(this.getEuropeButton());
        buttons.push(this.getUaeButton());
        return buttons;
      }

      getUaeButton() {
        return {
          label: window.geolocationStrings?.uaeCountryCurrency || "United Arab Emirates (AED)",
          onClick: () => this.goToUaeStore()
        };
      }

      getEuropeButton() {
        return {
          label: window.geolocationStrings?.euCountryCurrency || "Europe (€)",
          onClick: () => this.goToEuropeOption()
        };
      }

      getUkButton() {
        return {
          label: window.geolocationStrings?.ukCountryCurrency || "United Kingdom (£ GBP)",
          onClick: () => this.goToUkMarket()
        };
      }

      getDetectedMarketButton() {
        const suggestedCountry = this.countries.find(
          (country) => country.code === this.suggestions.code
        );

        if (!suggestedCountry) {
          return this.getUkButton();
        }

        return {
          label: `${suggestedCountry.name} (${suggestedCountry.currency})`,
          onClick: () => this.submitLocalization({
            code: this.suggestions.code,
            language: this.suggestions.language
          })
        };
      }

      goToUaeStore() {
        this.setToken(
          this.buildToken({
            selectedRegion: "AE"
          })
        );

        if (window.routes?.uae_redirect_url) {
          window.location.href = window.routes.uae_redirect_url;
        }
      }

      goToEuropeOption() {
        this.setToken(
          this.buildToken({
            selectedRegion: "EU"
          })
        );

        if (this.isPrimaryStore()) {
          const fallbackEuCountry = this.isEuropeanCountry(this.suggestions.code)
            ? this.suggestions.code
            : "DE";

          this.submitLocalization({
            code: fallbackEuCountry,
            language: this.suggestions.language || "en"
          });
          return;
        }

        if (window.routes?.uk_redirect_url) {
          window.location.href = window.routes.uk_redirect_url;
        }
      }

      goToUkMarket() {
        this.setToken(
          this.buildToken({
            selectedRegion: "GB"
          })
        );

        if (this.isPrimaryStore()) {
          this.submitLocalization({
            code: "GB",
            language: "en"
          });
          return;
        }

        if (window.routes?.uk_redirect_url) {
          window.location.href = window.routes.uk_redirect_url;
        }
      }

      submitLocalization(location) {
        this.setToken(
          this.buildToken({
            selectedRegion: location.code,
            language: location.language
          })
        );

        this.closeModal();

        const formId = crypto.randomUUID();
        const formHtml = `
          <form id="${formId}" action="/localization" method="POST" hidden>
            <input name="_method" value="PUT">
            <input name="country_code" value="${location.code}">
            <input name="language_code" value="${location.language}">
          </form>
        `;

        document.body.insertAdjacentHTML("beforeend", formHtml);
        document.getElementById(formId).submit();
      }

      close() {
        this.setToken(
          this.buildToken({
            selectedRegion: "dismissed"
          })
        );

        this.closeModal();
      }

      openModal() {
        this.classList.add("location-modal--active");
      }

      closeModal() {
        this.classList.remove("location-modal--active");
      }

      setToken(value) {
        window.localStorage.setItem(this.tokenName, JSON.stringify(value));
      }

      getToken(key) {
        const value = window.localStorage.getItem(key);

        if (!value) return null;

        try {
          return JSON.parse(value);
        } catch (error) {
          return null;
        }
      }
    }
  );
}
